import * as React from 'react';
import type { SwipeableHandlers } from 'react-swipeable';
import { forwardStyle, styled, type ForwardStyleProps, type StyledDecorator } from '@glitz/react';
import Swipe from './swipe';
import { animate } from '../animations';
import Overlay from '../overlay';
export { useStopPropagationOnSwipe } from './swipe';

export enum Position {
  Left,
  Right,
  Top,
  Bottom,
}

const flyoutDecorator = styled({
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  backgroundColor: 'white',
  willChange: 'transform',
});

const Right = styled.div(flyoutDecorator, {
  left: '100%',
  top: 0,
  bottom: 0,
  width: '440px',
});

const Left = styled.div(flyoutDecorator, {
  right: '100%',
  top: 0,
  bottom: 0,
  width: '440px',
});

const Top = styled.div(flyoutDecorator, {
  bottom: '100%',
  left: 0,
  right: 0,
});

const Bottom = styled.div(flyoutDecorator, {
  top: '100%',
  left: 0,
  right: 0,
});

function getComponent(position: Position) {
  switch (position) {
    case Position.Top:
      return Top;
    case Position.Bottom:
      return Bottom;
    case Position.Left:
      return Left;
    case Position.Right:
      return Right;
    default:
      return Right;
  }
}

function transformVisible(position: Position) {
  switch (position) {
    case Position.Top:
      return 'translateY(100%)';
    case Position.Bottom:
      return 'translateY(-100%)';
    case Position.Left:
      return 'translateX(100%)';
    case Position.Right:
      return 'translateX(-100%)';
    default:
      return 'translateX(100%)';
  }
}

function transformHidden(position: Position) {
  return position === Position.Top || position === Position.Bottom ? 'translateY(0)' : 'translateX(0)';
}

type PropType = {
  visible: boolean;
  position: Position;
};

type BasePropType = PropType & {
  visible: boolean;
  position: Position;
  onMouseDown: SwipeableHandlers['onMouseDown'];
  swipeRef: SwipeableHandlers['ref'];
};

const Base = styled(({ children, visible, position, onMouseDown, swipeRef }: React.PropsWithChildren<BasePropType>) => {
  const elementRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (visible) {
      const element = elementRef.current!;
      animate(element, [{ transform: transformHidden(position) }, { transform: transformVisible(position) }], {
        duration: 200,
      });

      return () => {
        let transform = getComputedStyle(element).transform;

        if (transform === 'none') {
          transform = transformVisible(position);
        }

        animate(element, [{ transform }, { transform: transformHidden(position) }]);
      };
    }
  }, [position, visible]);

  const Component = getComponent(position);

  return (
    <Component
      onMouseDown={onMouseDown}
      css={visible ? { transform: transformVisible(position) } : undefined}
      ref={element => {
        elementRef.current = element;
        swipeRef(element);
      }}
    >
      {children}
    </Component>
  );
});

type FlyoutPropType = PropType & {
  onClose(): void;
  keepChildrenMounted?: boolean;
  zIndex: number;
  overlayDecorator?: StyledDecorator;
  disableFreezeScroll?: boolean;
};

const Flyout = styled(
  forwardStyle(
    ({
      children,
      visible,
      position,
      onClose,
      compose,
      keepChildrenMounted = false,
      zIndex,
      overlayDecorator,
      disableFreezeScroll,
    }: React.PropsWithChildren<FlyoutPropType> & ForwardStyleProps) => {
      return (
        <Overlay
          zIndex={zIndex}
          keepChildrenMounted={keepChildrenMounted}
          isEnabled={visible}
          onClose={onClose}
          css={overlayDecorator}
          disableFreezeScroll={disableFreezeScroll}
        >
          <Swipe position={position} onClose={onClose}>
            {({ onMouseDown, ref }) => (
              <Base visible={visible} position={position} onMouseDown={onMouseDown} swipeRef={ref} css={compose()}>
                {children}
              </Base>
            )}
          </Swipe>
        </Overlay>
      );
    },
  ),
);

export default Flyout;
